import type { TextFieldOnChangeArguments } from 'uikit';
import { IconAlert, IconQuestion, Loader, SelectDropdown, SelectItem, Text, TextField, Tooltip } from 'uikit';

import type { IconPropSize } from '@consta/icons/__internal__/src/icons/Icon/helpers';
import cn from 'classnames';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Control, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { ComponentWithTooltip } from '../ComponentWithTooltip';

import type { FormulaEditorObjectSource } from './hooks';
import { getStringWithShieldsCreator, useAutoComplete, useColoredString } from './hooks';

import Styles from './FormulaEditor.module.css';

export const FormulaEditorField = ({
    control,
    objectSource,
    name,
    tooltipTitle,
    tooltipTitleClassName,
    tooltipMessage,
    alertTooltipMessage,
    placeholderMessage,
    isDisabled,
    isReadOnly,
    isRequired,
    isLoading,
    wrapperClassName,
    rules,
    isTooltipHidden,
    getSelectItemLabel,
    isDotNotationVariables = true,
    isAvailableWithEmptyVariables = false,
    iconTitleSize = 'm',
    maxIntegerPartLength,
    maxFractionalPartLength,
}: {
    control: Control;
    objectSource: FormulaEditorObjectSource | undefined;
    name: string;
    tooltipTitle?: string;
    tooltipTitleClassName?: string;
    placeholderMessage?: string;
    tooltipMessage?: string | ReactElement;
    alertTooltipMessage?: string | ReactElement;
    isDisabled?: boolean;
    isReadOnly?: boolean;
    isRequired?: boolean;
    isLoading?: boolean;
    wrapperClassName?: string;
    isTooltipHidden?: boolean;
    getSelectItemLabel?: (item: string) => any;
    isDotNotationVariables?: boolean;
    isAvailableWithEmptyVariables?: boolean;
    iconTitleSize?: IconPropSize;
    maxIntegerPartLength?: number;
    maxFractionalPartLength?: number;
} & Pick<UseControllerProps<any>, 'rules'>) => {
    const dropdownRef = useRef(null);
    const containerRef = useRef(null);
    const [showErrors, setShowErrors] = useState(true);

    const objectSourceNotExists = useMemo(() => !objectSource || !Object.keys(objectSource).length, [objectSource]);

    const controllerFinaleRules = useMemo(() => {
        if (objectSourceNotExists && isRequired) {
            return {
                required: `Нет данных о переменных.
                 Добавление переменных невозможно.`,
            };
        }

        return rules;
    }, [isRequired, objectSourceNotExists, rules]);

    const {
        field,
        fieldState,
        formState: { submitCount },
    } = useController({
        name,
        control,
        rules: controllerFinaleRules,
    });

    useEffect(() => {
        if (submitCount) {
            setShowErrors(true);
        }
    }, [submitCount]);

    /*
     * локально для FormulaEditorField делаем всю подсветку ошибок только после нажатия на кнопку отправки формы
     * после изменения формулы подсветка и тултип скрываются до следующей отправки формы
     */
    const errorsVisible = fieldState.invalid && showErrors;
    const coloredString = useColoredString({
        field,
        isInvalidStatus: errorsVisible,
        isTooltipHidden: isTooltipHidden || isDisabled,
        objectSource,
        isAvailableWithEmptyVariables,
        maxIntegerPartLength,
        maxFractionalPartLength,
    });

    const { isOpen, inputRef, onKeyUpHandler, getOptionProps, visibleItems, hasItems, coloredRef } = useAutoComplete({
        source: objectSource,
        field,
        dropdownRef,
        controlRef: containerRef,
        isDotNotationVariables,
    });

    const getStringWithShields = useMemo(() => getStringWithShieldsCreator({ objectSource }), [objectSource]);

    const onChangeHandler = useCallback(
        (agrs: TextFieldOnChangeArguments) => {
            const { value } = agrs;

            const newString =
                getStringWithShields({
                    fullStringValue: value,
                }) ?? '';

            onKeyUpHandler(agrs);
            setShowErrors(false);
            field.onChange(newString);
        },
        [field, getStringWithShields, onKeyUpHandler],
    );

    const renderItemDefault = useCallback(
        (propsRenderItem: any) => {
            const { item, active, hovered, onClick, onMouseEnter } = propsRenderItem;

            const label = getSelectItemLabel ? getSelectItemLabel(item) : item;

            return (
                <SelectItem
                    label={label}
                    active={active}
                    hovered={hovered}
                    multiple={false}
                    disabled={false}
                    size="xs"
                    indent="normal"
                    onClick={onClick}
                    onMouseEnter={onMouseEnter}
                />
            );
        },
        [getSelectItemLabel],
    );

    const makeInputInFocus = useCallback(() => {
        inputRef.current?.focus();
    }, [inputRef]);

    const withTooltipBox = useMemo(() => !!tooltipMessage || !!tooltipTitle, [tooltipMessage, tooltipTitle]);

    const isDisabledByVariables = useMemo(
        () => !isAvailableWithEmptyVariables && objectSourceNotExists,
        [isAvailableWithEmptyVariables, objectSourceNotExists],
    );

    const isTextFieldDisabled = useMemo(
        () => isDisabled || isReadOnly || isLoading || isDisabledByVariables,
        [isDisabled, isDisabledByVariables, isLoading, isReadOnly],
    );

    return (
        <div className={Styles.root}>
            {withTooltipBox && (
                <div className={Styles.tooltipBox}>
                    {tooltipTitle && (
                        <Text as="span" view="secondary" className={cn(tooltipTitleClassName, Styles.tooltipTitle)}>
                            {tooltipTitle}
                        </Text>
                    )}

                    {alertTooltipMessage && objectSourceNotExists && isRequired && (
                        <ComponentWithTooltip tooltipText={alertTooltipMessage}>
                            <IconAlert view="alert" size={iconTitleSize} />
                        </ComponentWithTooltip>
                    )}
                    {tooltipMessage && (!objectSourceNotExists || isAvailableWithEmptyVariables) && (
                        <ComponentWithTooltip tooltipText={tooltipMessage}>
                            <IconQuestion view="secondary" size={iconTitleSize} />
                        </ComponentWithTooltip>
                    )}
                </div>
            )}
            <div
                role="presentation"
                className={cn(wrapperClassName, Styles.wrapper, {
                    [Styles.isDisabled]: isDisabled || isLoading || isDisabledByVariables,
                    [Styles.isInvalid]: errorsVisible,
                })}
                onClick={makeInputInFocus}
            >
                <TextField
                    id={name}
                    name={name}
                    value={field.value}
                    inputRef={inputRef}
                    inputContainerRef={containerRef}
                    onChange={onChangeHandler}
                    width="full"
                    type="textarea"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                    autoCapitalize="off"
                    status={errorsVisible ? 'alert' : undefined}
                    className={Styles.textField}
                    disabled={isTextFieldDisabled}
                    readOnly={isReadOnly}
                    required={isRequired}
                    placeholder={placeholderMessage}
                    maxRows={10}
                />

                {!isTooltipHidden && errorsVisible && fieldState.error?.message && (
                    <Tooltip size="m" direction="rightCenter" status="alert" anchorRef={containerRef}>
                        {fieldState.error?.message.split('\n').map((errString, errIdx) => (
                            <div key={errIdx} className="Text Text_lineHeight_m Text_size_xs Text_view_primary">
                                {errString}
                            </div>
                        ))}
                    </Tooltip>
                )}

                <SelectDropdown
                    isOpen={isOpen}
                    size="xs"
                    controlRef={containerRef}
                    getOptionProps={getOptionProps}
                    dropdownRef={dropdownRef}
                    form="default"
                    renderItem={renderItemDefault}
                    visibleItems={visibleItems}
                    hasItems={hasItems}
                />

                <div className={Styles.styledContainer}>
                    <div ref={coloredRef} className={Styles.styled}>
                        {coloredString}
                    </div>
                </div>
                {isLoading && <Loader />}
            </div>
        </div>
    );
};

FormulaEditorField.displayName = 'FormulaEditorField';
